/**
 * Auto Generator Nomor Purchase Order (PO) Satala Dermatech Essential
 * 
 * Format Baru: [PERUSAHAAN_PEMESAN]-PO-[SINGKATAN_SUPPLIER]-[DDMMYY]-[COUNTER_3DIGIT]
 * Contoh: 
 * - SDE-PO-AM-110826-001 (Order ke-1 ke Supplier AM bulan Ags 2026 oleh SDE)
 * - ASB-PO-AM-110826-002 (Order ke-2 ke Supplier AM bulan Ags 2026 oleh ASB)
 * 
 * Catatan:
 * - Counter berlanjut untuk supplier yang sama pada bulan tersebut (antar pemesan).
 * - Setiap berganti bulan baru (MMYY), counter di-reset kembali ke 001.
 */

// Helper 1: Inisial Perusahaan Pemesan (SDE, ASB, etc.)
export function getCompanyAbbr(compName) {
  if (!compName || !compName.trim()) return 'SDE';
  const trimmed = compName.trim();
  
  // Jika pengguna langsung memasukkan inisial pendek 2-5 huruf (misal "SDE", "ASB")
  if (/^[A-Za-z0-9]{2,5}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  const mainName = trimmed.split('(')[0].trim();
  const cleaned = mainName
    .replace(/PT |CV |LTD |INC |Toko /gi, '')
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  
  if (words.length >= 3) {
    return words.slice(0, 3).map(w => w[0]).join('').toUpperCase();
  } else if (words.length === 2) {
    return (words[0].slice(0, 2) + words[1][0]).toUpperCase(); // ASIA BEAUTY => ASB
  } else if (words.length === 1) {
    return words[0].slice(0, 3).toUpperCase();
  }
  return 'SDE';
}

// Helper 2: Singkatan Supplier Maksimal 3 Huruf (AM, MSI, etc.)
export function getSupplierAbbr(salesName) {
  if (!salesName || !salesName.trim()) return 'XXX';
  const trimmed = salesName.trim();
  
  const mainName = trimmed.split('(')[0].trim();
  const cleaned = mainName
    .replace(/PT |CV |LTD |INC |Toko /gi, '')
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  
  if (words.length >= 3) {
    return words.slice(0, 3).map(w => w[0]).join('').toUpperCase();
  } else if (words.length === 2) {
    return words.map(w => w[0]).join('').toUpperCase(); // ADHITAMA MANDIRI => AM
  } else if (words.length === 1) {
    return words[0].slice(0, 3).toUpperCase();
  }
  return 'XXX';
}

// Main Generator
export function generatePONumber(options = {}) {
  let buyerName = '';
  let salesName = '';
  let existingRequests = [];
  let date = new Date();

  if (typeof options === 'string') {
    salesName = options;
  } else if (typeof options === 'object' && options !== null) {
    buyerName = options.buyerName || options.company_name || options.pemesanNama || '';
    salesName = options.salesName || options.nama_sales || options.nama_supplier || '';
    existingRequests = options.existingRequests || options.existingOrders || options.requests || [];
    date = options.date ? new Date(options.date) : new Date();
  }

  const compAbbr = getCompanyAbbr(buyerName);
  const suppAbbr = getSupplierAbbr(salesName);

  const validDate = date && !isNaN(new Date(date).getTime()) ? new Date(date) : new Date();
  const dd = String(validDate.getDate()).padStart(2, '0');
  const mm = String(validDate.getMonth() + 1).padStart(2, '0');
  const yy = String(validDate.getFullYear()).slice(-2);
  const dateTag = `${dd}${mm}${yy}`; // e.g. "110826"

  let maxSeq = 0;
  const targetMonth = validDate.getMonth();
  const targetYear = validDate.getFullYear();
  const supplierKey = salesName ? salesName.toLowerCase().replace(/PT |CV |LTD |INC |Toko /gi, '').split('(')[0].trim() : '';

  if (Array.isArray(existingRequests) && existingRequests.length > 0) {
    existingRequests.forEach(req => {
      if (!req) return;
      const reqSales = req.nama_sales || req.supplier_name || req.nama_perusahaan || '';
      const reqSalesKey = reqSales ? reqSales.toLowerCase().replace(/PT |CV |LTD |INC |Toko /gi, '').split('(')[0].trim() : '';
      const reqPo = req.po_number || req.order_number || req.no_po || '';
      
      const reqDateStr = req.created_date || req.created_at || req.date;
      const reqDate = reqDateStr ? new Date(reqDateStr) : null;
      const isSameMonthYear = reqDate && !isNaN(reqDate.getTime()) && 
                              reqDate.getMonth() === targetMonth && 
                              reqDate.getFullYear() === targetYear;

      if (reqPo) {
        // Match format baru: [BUYER]-PO-[SUPPLIER]-[DDMMYY]-[SEQ]
        const matchNew = reqPo.match(/^[A-Z0-9]+-PO-([A-Z0-9]+)-(\d{2})(\d{2})(\d{2})-(\d{3})/i);
        if (matchNew) {
          const [, pSuppAbbr, pDd, pMm, pYy, pSeq] = matchNew;
          const pMonth = parseInt(pMm, 10) - 1;
          const pYear = 2000 + parseInt(pYy, 10);

          if ((pSuppAbbr.toUpperCase() === suppAbbr || (supplierKey && reqSalesKey && reqSalesKey === supplierKey)) &&
              pMonth === targetMonth && pYear === targetYear) {
            const seq = parseInt(pSeq, 10);
            if (!isNaN(seq) && seq > maxSeq) {
              maxSeq = seq;
            }
          }
        }
      } else if (supplierKey && reqSalesKey && reqSalesKey === supplierKey && isSameMonthYear) {
        maxSeq++;
      }
    });
  }

  const nextSeq = String(maxSeq + 1).padStart(3, '0');
  return `${compAbbr}-PO-${suppAbbr}-${dateTag}-${nextSeq}`;
}

export function generateNextPoNumber(existingOrders = [], date = new Date()) {
  return generatePONumber({ existingRequests: existingOrders, date });
}
